/**
 * useReservationHistory — TanStack `useInfiniteQuery` over
 * `GET /organizations/{slug}/reservations/{id}/history` (SCRUM-338; A.5 deep
 * pagination).
 *
 * The reservation entity-change timeline. The BE paginates by keyset cursor
 * (`next_cursor`); this hook accumulates every fetched page and exposes the
 * flat-mapped `items`, plus a `fetchMore` control to pull older changes. It
 * mirrors `use-user-audit.ts` — the infinite-query + Symbol-sentinel precedent —
 * and keeps the SAME five-branch status union `loading | ready | forbidden |
 * notFound | error` and the SAME org-scoped queryKey base as before.
 *
 * A 404 → `notFound` (anti-enumeration: the BE returns 404 for an absent OR
 * cross-org reservation from a readability pre-check) — a DISTINCT, non-error
 * state the tab renders as honest-empty ("no changes recorded yet"), NOT the
 * generic Retry affordance.
 *
 * The row `EntityChangeRecordDto.occurred_at` is **epoch-ms** (a number, NOT an
 * ISO string). `actor_display_name` is the resolved operator label (display name,
 * else email) or null = "System" (SCRUM-349); the `semantic_label_key` carries a
 * lifecycle verb key (SCRUM-338 A.1) or null.
 *
 *   - reads the org slug via `useCurrentOrg()` (RLS-scoped per CLAUDE.md
 *     non-negotiable §1)
 *   - `queryKey = [...bookingQueryKeys.history(id), orgSlug]` (nested under
 *     `detail(id)` so a detail-scoped invalidation cascades)
 *   - `enabled: orgSlug !== null && reservationId != null`
 *   - `retry: false`
 *   - the 200 body IS the `ReservationHistoryPageDto` DIRECTLY (NOT wrapped in a
 *     `{ data }` envelope) — the Symbol sentinels (403/404) are the ONLY page
 *     values that are not a page; `getNextPageParam` and the item flat-map both
 *     guard against a sentinel page (it never yields a cursor nor an item)
 *   - every branch carries a `refetch` function; `ready` also exposes
 *     `fetchMore` / `isFetchingMore` / `hasMore` / `projectionCaughtUp`
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { EntityChangeRecordDto, ReservationHistoryPageDto } from './types.js';
import { bookingQueryKeys } from './queryKeys.js';

/** Per-request page size for the keyset history read. */
const HISTORY_PAGE_LIMIT = 50;

/**
 * 403 sentinel — the caller lacks `booking.reservations.read`. The tab renders
 * a permission-denied panel (no Retry). Own module-level Symbol, distinct from
 * the detail hook's `FORBIDDEN`.
 */
export const HISTORY_FORBIDDEN = Symbol('reservation-history:forbidden');

/**
 * 404 sentinel — the reservation is absent OR belongs to another org (the BE
 * returns 404 for both, anti-enumeration). A distinct, non-error state: the tab
 * renders honest-empty.
 */
export const HISTORY_NOT_FOUND = Symbol('reservation-history:notFound');

/** One fetched page is EITHER a real history page OR a terminal Symbol sentinel. */
type HistoryPageResult =
  | typeof HISTORY_FORBIDDEN
  | typeof HISTORY_NOT_FOUND
  | ReservationHistoryPageDto;

/** A Symbol sentinel is a terminal page — never a real `ReservationHistoryPageDto`. */
function isSentinel(
  page: HistoryPageResult,
): page is typeof HISTORY_FORBIDDEN | typeof HISTORY_NOT_FOUND {
  return page === HISTORY_FORBIDDEN || page === HISTORY_NOT_FOUND;
}

export type UseReservationHistoryResult =
  | {
      readonly status: 'loading';
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      /** The ACCUMULATED, flat-mapped items across every fetched page. */
      readonly items: readonly EntityChangeRecordDto[];
      /** Projection freshness from the LAST fetched page. */
      readonly projectionCaughtUp: boolean;
      /** True when there is a further (older) page to fetch. */
      readonly hasMore: boolean;
      /** Pull the next (older) page. */
      readonly fetchMore: () => void;
      /** True while a further page is in flight. */
      readonly isFetchingMore: boolean;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly refetch: () => void;
    }
  | {
      readonly status: 'notFound';
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly refetch: () => void;
    };

export function useReservationHistory(
  reservationId: string | null,
): UseReservationHistoryResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const enabled = orgSlug !== null && reservationId != null;

  const query = useInfiniteQuery<
    HistoryPageResult,
    Error,
    { pages: HistoryPageResult[]; pageParams: unknown[] },
    readonly unknown[],
    string | undefined
  >({
    // `history(id)` requires a string; when id is null the query is disabled so
    // the key is never used — an empty string keeps the tuple type stable.
    queryKey: [...bookingQueryKeys.history(reservationId ?? ''), orgSlug],
    enabled,
    retry: false,
    initialPageParam: undefined,
    // A sentinel page never yields a cursor (guard first — reading `.next_cursor`
    // off a Symbol would crash); a page whose next_cursor is null → no more.
    getNextPageParam: (lastPage) =>
      isSentinel(lastPage) ? undefined : (lastPage.next_cursor ?? undefined),
    queryFn: async ({ pageParam }) => {
      const query: Record<string, string | number> = { limit: HISTORY_PAGE_LIMIT };
      if (typeof pageParam === 'string' && pageParam.length > 0) {
        query.cursor = pageParam;
      }
      const result = await apiClient.GET(
        '/organizations/{slug}/reservations/{id}/history',
        {
          params: {
            path: { slug: orgSlug as string, id: reservationId as string },
            query: query as never,
          },
        },
      );
      if (result.response.status === 403) {
        return HISTORY_FORBIDDEN;
      }
      if (result.response.status === 404) {
        return HISTORY_NOT_FOUND;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/reservations/${reservationId}/history failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };
  const fetchMore = (): void => {
    void query.fetchNextPage();
  };

  if (!enabled || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }

  const pages = query.data?.pages ?? [];
  const first = pages[0];
  // A first-page sentinel short-circuits: the whole result is that terminal state
  // (the sentinel never flows into `items` nor `getNextPageParam`).
  if (first === HISTORY_FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }
  if (first === HISTORY_NOT_FOUND) {
    return { status: 'notFound', refetch };
  }

  // Flat-map every real page's items; skip any sentinel defensively.
  const items: EntityChangeRecordDto[] = [];
  let projectionCaughtUp = true;
  for (const page of pages) {
    if (isSentinel(page)) continue;
    for (const item of page.items) items.push(item);
    projectionCaughtUp = page.projection_caught_up;
  }

  const lastPage = pages[pages.length - 1];
  const hasMore =
    query.hasNextPage && lastPage !== undefined && !isSentinel(lastPage);

  return {
    status: 'ready',
    items,
    projectionCaughtUp,
    hasMore,
    fetchMore,
    isFetchingMore: query.isFetchingNextPage,
    refetch,
  };
}
